// src/components/PasswordReset.vue
<template>
  <div class="container">
    <div class="row justify-content-center vstack align-items-center">
      <div class="col-md-6 col-lg-5 alert-banner-message" v-if="user.is_password_force_reset">
        <div class="alert alert-danger d-flex align-items-center p-3 rounded" role="alert">
          <!-- Error Circle Icon -->
          <div class="me-3">
              <div class="d-flex align-items-center justify-content-center bg-white rounded-circle" style="width: 40px; height: 40px;">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" class="bi bi-exclamation-circle text-danger" viewBox="0 0 16 16">
                      <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/>
                      <path d="M7.002 11a1 1 0 1 1 2 0 1 1 0 0 1-2 0zM7.1 4.995a.905.905 0 1 1 1.8 0l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 4.995z"/>
                  </svg>
              </div>
          </div>

          <!-- Message Content -->
          <div>
            <h6 class="alert-heading mb-1 fw-semibold">System Required Password Update</h6>
            <p class="mb-0 text-muted">For security purposes, you are required to update your password before continuing.</p>
          </div>
      </div>
      </div>
      <div class="col-md-6 col-lg-5">
        <div class="text-center mb-4">
          <!-- Icon -->
          <div class="d-inline-flex align-items-center justify-content-center bg-light rounded-3 p-3 mb-3">
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              width="24" 
              height="24" 
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
              style="color: #FF7F00"
            >
              <path 
                stroke-linecap="round" 
                stroke-linejoin="round" 
                stroke-width="2" 
                d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
              />
            </svg>
          </div>

          <!-- Title -->
          <h4 class="mb-2">Create a new password</h4>
          <p class="text-muted small">
            Please choose a password that hasn't been used before. Must be at least 12 characters.
          </p>
        </div>

        <!-- Form -->
        <form @submit.prevent="handleSubmit">
          <div class="mb-3">
            <div class="form-floating">
              <input
                v-model="form.password"
                type="password"
                class="form-control"
                :class="{ 'is-invalid': v$.form.password.$error }"
                id="password"
                placeholder="Set new password"
                @input="v$.form.password.$touch"
              >
              <label for="password">Set new password</label>
              <div v-if="v$.form.password.$error" class="invalid-feedback">
                <li v-for="error in v$.form.password.$errors">{{  error.$message }}</li>
              </div>
            </div>
          </div>

          <div class="mb-4">
            <div class="form-floating">
              <input
                v-model="form.confirm_password"
                type="password"
                class="form-control"
                :class="{ 'is-invalid': v$.form.confirm_password.$error }"
                id="confirm_password"
                placeholder="Confirm new password"
                @input="v$.form.confirm_password.$touch"
              >
              <label for="confirm_password">Confirm new password</label>
              <div v-if="v$.form.confirm_password.$error" class="invalid-feedback">
                {{ v$.form.confirm_password.$errors[0].$message }}
              </div>
            </div>
          </div>

          <button
            type="submit"
            class="btn btn-primary w-100"
            :disabled="submitting"
          >
            <span 
              v-if="submitting" 
              class="spinner-border spinner-border-sm me-2" 
              role="status"
            ></span>
            {{ submitting ? 'Resetting...' : 'Reset password' }}
          </button>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, ref } from 'vue'
import { useVuelidate } from "@vuelidate/core"
import { required, minLength, sameAs, helpers } from "@vuelidate/validators"
import { passwordValidations } from "../helpers/passwordValidations"
import { useStore } from 'vuex';

export default {
  name: 'PasswordReset',
  
  setup() {
    const submitting = ref(false)
    const v$ = useVuelidate()
    const store = useStore();

    let user = computed(() => store.state.user);

    const form = ref({
      password: null,
      confirm_password: null
    })

    return {
      form,
      submitting,
      v$,
      user,
      store
    }
  },

  validations() {
    return {
      form: {
        password: {
          required: helpers.withMessage('Password is required', required),
          minLength: helpers.withMessage(
            'Password must be at least 12 characters', 
            minLength(12)
          ),
          ...passwordValidations
        },
        confirm_password: {
          required: helpers.withMessage(
            'Please confirm your password', 
            required
          ),
          sameAsPassword: helpers.withMessage(
            'Passwords must match', 
            sameAs(this.form.password)
          )
        }
      }
    }
  },

  methods: {
    async handleSubmit() {
      try {
        window.emitter.emit("changeLoaderStatus", true);

        await this.store.dispatch('resetPassword', {
          password: this.form.password,
          password_confirmation: this.form.confirm_password
        });

        this.form.confirm_password = null;
      } catch (error) {
        // Error already handled by store
        console.error('Password reset failed:', error);
      }
    }

  }
}
</script>
<style scoped>
.btn-primary {
  --bs-btn-bg: #FF7F00;
  --bs-btn-border-color: #FF7F00;
  --bs-btn-hover-bg: #cc6600;
  --bs-btn-hover-border-color: var(--bs-btn-hover-bg);
}
</style>